import styles from '../subscriptionSettings.module.scss';

const LOCALE_ORDER = ['EN', 'FR'];

const LOCALE_LABELS = {
  EN: 'English',
  FR: 'French',
};

const sortLocaleEntries = entries =>
  [...entries].sort(([a], [b]) => {
    const ia = LOCALE_ORDER.indexOf(a);
    const ib = LOCALE_ORDER.indexOf(b);
    if (ia !== -1 || ib !== -1) {
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    }
    return a.localeCompare(b);
  });

export const getLocalizedEntries = value => {
  if (value === null || value === undefined) {
    return [];
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const text = `${value}`.trim();
    return text ? [['', text]] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(item => getLocalizedEntries(item));
  }

  if (typeof value === 'object') {
    return sortLocaleEntries(
      Object.entries(value).filter(([, v]) => {
        if (v === null || v === undefined) {
          return false;
        }
        if (typeof v === 'object') {
          return false;
        }
        return `${v}`.trim() !== '';
      }),
    );
  }

  return [];
};

export const getPrimaryLocaleText = value => {
  const entries = getLocalizedEntries(value);
  if (!entries.length) {
    return '-';
  }
  const preferred = entries.find(([code]) => LOCALE_ORDER.includes(code));
  const [, text] = preferred || entries[0];
  return text || '-';
};

export const formatDuration = duration => {
  if (!duration?.value || !duration?.unit) {
    return null;
  }

  const unit = `${duration.unit}`.toLowerCase();
  const value = duration.value;
  const unitLabel = value === 1 ? unit.replace(/s$/, '') : `${unit}s`;
  return `${value} ${unitLabel}`;
};

export const humanizeKey = key => {
  if (!key || typeof key !== 'string') {
    return '-';
  }

  if (key === 'ONE_OFF') {
    return 'One-time Subscription';
  }

  if (key === 'TIME_BASED') {
    return 'Time Based Subscription';
  }

  return key
    .split('_')
    .map(part => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
};

export const normalizeSubscriptionType = value =>
  `${value ?? ''}`
    .trim()
    .replace(/[^A-Za-z0-9\s_]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();

export const getRenewalLabel = item => {
  if (item?.purchaseType === 'ONE_OFF') {
    return null;
  }

  return item?.allowRecurring ? 'Auto-renews' : 'Manual renewal';
};

export const getCouponLabel = allowCoupon =>
  allowCoupon ? 'Coupons accepted' : 'No coupons';

const JsonLocaleBlock = ({value}) => {
  const entries = getLocalizedEntries(value);
  if (!entries.length) {
    return null;
  }

  return (
    <div className={styles['locale-block']}>
      {entries.map(([code, text]) => (
        <div key={code || 'default'} className={styles['locale-row']}>
          {code ? (
            <span className={styles['locale-code']}>
              {LOCALE_LABELS[code] || code}
            </span>
          ) : null}
          <span className={styles['locale-text']}>{text}</span>
        </div>
      ))}
    </div>
  );
};

export const renderLocalizedOrDash = value =>
  getLocalizedEntries(value).length ? (
    <JsonLocaleBlock value={value} />
  ) : (
    <span className={styles['empty-json']}>-</span>
  );

const formatMoney = amount =>
  typeof amount === 'number'
    ? amount.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })
    : amount ?? '-';

const getDiscountBadge = discount => {
  if (!discount || discount?.type === 'NONE') {
    return null;
  }

  if (discount?.value === null || discount?.value === undefined) {
    return null;
  }

  if (discount.type === 'PERCENTAGE') {
    return `-${discount.value}%`;
  }

  return `-${formatMoney(discount.value)}`;
};

export const PriceSummary = ({prices}) => {
  if (!prices?.length) {
    return <span className={styles['empty-json']}>-</span>;
  }

  return (
    <div className={styles['price-summary']}>
      {prices.slice(0, 4).map((price, index) => (
        <span
          key={`${price?.currency}-${index}`}
          className={styles['price-pill']}>
          <span className={styles['price-pill-currency']}>
            {price?.currency}
          </span>
          <span className={styles['price-pill-amount']}>
            {formatMoney(price?.amount)}
          </span>
          {typeof price?.originalAmount === 'number' &&
          typeof price?.amount === 'number' &&
          price.originalAmount > price.amount ? (
            <span className={styles['price-pill-original']}>
              {formatMoney(price.originalAmount)}
            </span>
          ) : null}
          {getDiscountBadge(price?.discount) ? (
            <span className={styles['price-pill-discount']}>
              {getDiscountBadge(price.discount)}
            </span>
          ) : null}
        </span>
      ))}
      {prices.length > 4 ? (
        <span className={styles['price-more']}>+{prices.length - 4} more</span>
      ) : null}
    </div>
  );
};
